// tslint:disable: variable-name
import {Component, OnInit} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {LeasingService} from './leasing.service';
import {IFormData} from './models/formData';
import {Messages} from './i18n/messages';
import {IIdNameItem} from './models/IdNameItem';
import {ISliderConfig} from './models/sliderConfig';
import {MatDialog} from '@angular/material/dialog';
import {LeasingCriteria} from './models/leasingCriteria';
import {LeasingCarCriteria} from './models/leasingCarCriteria';
import {SummaryDialogComponent} from './summaryDialog.component';
import {ILeasingSearchResult} from './models/leasingSearchResult';
import {ILeasingSearchResultCustomer} from './models/leasingSearchResultCustomer';
import {FeedbackDialogComponent} from './feedbackDialog.component';
import {ChangeLocationDialogComponent} from './changeLocationDialog.component';
import {FirstClickBudgetDialogComponent} from './firstClickBudgetDialog.component';
import {ChangeBudgetDialogComponent} from './changeBudgetDialog.component';
import {UsedCarsNotAcceptedDialogComponent} from './usedCarsNotAcceptedDialog.component';
import {animate, state, style, transition, trigger} from '@angular/animations';

declare const onSubmitDone: any;

@Component({
    selector: 'app-leasing',
    templateUrl: './leasing.component.html',
    styleUrls: ['./leasing.component.css'],
    standalone: false,
    animations: [
        trigger('displayState', [
            state('false', style({overflow: 'hidden', height: '0px', padding: '0'})),
            state('true', style({overflow: 'hidden', height: '*'})),
            transition('false => true', animate('200ms ease-in')),
            transition('true => false', animate('200ms ease-out'))
        ]),
    ]
})
export class LeasingComponent implements OnInit {

    // the main form
    leasingForm: FormGroup;

    /** list of locations filtered by search keyword */
    public filteredLocations: IIdNameItem[];

    /** control for the MatSelect filter keyword */
    public locationFilterCtrl: FormControl = new FormControl();

    // data model properties
    formData: IFormData;

    // stored the search criteria
    leasingCriteria: LeasingCriteria;

    // last search result
    searchResult: ILeasingSearchResult;

    monthlyFeeSliderConfig: ISliderConfig;

    // list of form control names that should be ignored when reseting
    ignoredControlNamesOnReset = ['contactPerson'];

    // holds the current visible section. Only one is visibe at a certain moment
    openedSections: string[] = ['location', 'contactPerson'];

    locationTooltipVisible = true;
    serviceTooltipVisible = false;
    usedCarTooltipVisible = true;

    presetCarWasRemoved: boolean = false;

    makeAndModelWasDisabled: boolean = false;

    get locations(): IIdNameItem[] {
        if (this.searchResult.search.location.length === 0) {
            return this.formData.municipalities;
        }
        return this.formData.municipalities.filter(location => this.searchResult.search.location.includes(location.id));
    }

    get carSpecs(): FormArray {
        return this.leasingForm.get('carSpecs') as FormArray;
    }

    get validCarSpecsCount(): number {
        let validSpecsCount = 0;
        this.carSpecs.controls.forEach(formGroup => {
            if (formGroup.valid) {
                validSpecsCount++;
            }
        });
        return validSpecsCount;
    }

    get totalCustomersCount(): number {
        return this.allCustomerNames.length;
    }

    get allCustomers(): ILeasingSearchResultCustomer[] {
        const customers: ILeasingSearchResultCustomer[] = [];
        if (this.leasingCriteria.monthlyFee) {
            this.leasingCriteria.carSpecs.forEach(carSpec => {
                carSpec.searchResult.customers.forEach(customer => {
                    if (!customers.includes(customer)) {
                        customers.push(customer);
                    }
                });
            });
            return customers;
        }
        if (this.searchResult) {
            return this.searchResult.customers;
        }
        return [];
    }

    get allCustomerNames(): string[] {
        const customers: string[] = [];
        this.allCustomers.forEach(customer => {
            if (!customers.includes(customer.name)) {
                customers.push(customer.name);
            }
        });
        return customers;
    }

    get customerNamesNotAcceptingUsedCars(): string[] {
        const customers: string[] = [];
        this.allCustomers.forEach(customer => {
            if (!customer.acceptUsedCars && !customers.includes(customer.name)) {
                customers.push(customer.name);
            }
        });
        return customers;
    }

    get customerNamesAcceptingUsedCars(): string[] {
        const customers: string[] = [];
        this.allCustomers.forEach(customer => {
            if (customer.acceptUsedCars && !customers.includes(customer.name)) {
                customers.push(customer.name);
            }
        });
        return customers;
    }

    get noCustomerAcceptsUsedCars(): boolean {
        if (this.customerNamesNotAcceptingUsedCars) {
            return this.allCustomerNames.length == this.customerNamesNotAcceptingUsedCars.length;
        }
        return false;
    }

    get allCustomersAcceptUsedCars(): boolean {
        let allAcceptUserCars = true;
        this.allCustomers.forEach(customer => {
            if (!customer.acceptUsedCars) {
                allAcceptUserCars = false;
            }
        });
        return allAcceptUserCars;
    }

    constructor(private fb: FormBuilder, private leasingService: LeasingService, private dialog: MatDialog) {
    }

    ngOnInit(): void {
        this.leasingCriteria = this.initLeasingCriteria();

        this.monthlyFeeSliderConfig = new ISliderConfig();
        this.monthlyFeeSliderConfig.step = 100;
        this.monthlyFeeSliderConfig.unit = '€';

        // get initial form data
        this.leasingService.getDefaultFormData().subscribe({
            next: formData => {
                this.formData = formData;

                if (this.leasingCriteria.singleMunicipality) {
                    // show only the single municipality
                    const municipalities = this.formData.municipalities.filter((municipality) => {
                        return municipality.id === this.leasingCriteria.singleMunicipality;
                    });
                    this.leasingCriteria.locations = municipalities;

                    // if there is a single municipality in url, auto-select it
                    this.autoselectMunicipality();
                    this.changeLocation(this.leasingCriteria.singleMunicipality);
                    //scroll to monthly fee section
                    setTimeout(function () {
                        let el = document.getElementById('monthlyFeeSection');
                        el.scrollIntoView({behavior: "smooth"});
                    }, 100);

                    this.locationTooltipVisible = false;

                } else {
                    this.leasingCriteria.locations = this.formData.municipalities;
                }

                this.filteredLocations = this.leasingCriteria.locations.slice();

            },
            error: error => this.showErrorFeedback(error),
        });

        // create the form
        this.leasingForm = this.fb.group({
            location: [null, Validators.required],
            monthlyFee: [null, Validators.required],
            carSpecs: this.fb.array([]),
            contactPerson: this.createContactPersonGroup()
        });
        this.addOwnCarValidators(false);

        this.leasingForm.get('location').valueChanges.subscribe(value => this.changeLocation(value));
        this.locationFilterCtrl.valueChanges.subscribe(value => this.filterLocations(value));
    }

    initLeasingCriteria(): LeasingCriteria {
        const criteria = new LeasingCriteria();

        const url = new URL(window.location.href);

        const municipalityParam = url.searchParams.get("municipality");
        if (municipalityParam != null) {
            criteria.singleMunicipality = Number(municipalityParam);
        }

        const carMakeParam = url.searchParams.get("make");
        if (carMakeParam != null) {
            criteria.singleCarMake = Number(carMakeParam);
        }

        const carModelParam = url.searchParams.get("model");
        if (carModelParam != null) {
            criteria.singleCarModel = Number(carModelParam);
        }

        return criteria;
    }

    filterLocations(search: String) {
        if (!search) {
            this.filteredLocations = this.leasingCriteria.locations;
            return;
        } else {
            // filter the locations
            this.filteredLocations = this.leasingCriteria.locations.filter(location => location.name.toLowerCase().indexOf(search.toLowerCase()) > -1);
        }
    }

    createNewCarSpec(specIndex: number): FormGroup {
        const formGroup: FormGroup = this.fb.group({
            contractLength: [{value: null, disabled: false}, Validators.required],
            contractMileage: [{value: null, disabled: false}, Validators.required],
            make: [{value: null, disabled: false}, Validators.required],
            model: [{value: null, disabled: false}, Validators.required],
            bodyType: [{value: null, disabled: false}, Validators.required],
            fuelType: [{value: null, disabled: false}, Validators.required],
            services: this.fb.array([]),
            additionalInformation: [{value: '', disabled: false}, Validators.maxLength(500)],
        });

        formGroup.get('make').valueChanges.subscribe(value => this.changeMake(specIndex, value));
        formGroup.get('model').valueChanges.subscribe(value => this.changeModel(specIndex, value));
        formGroup.get('bodyType').valueChanges.subscribe(value => this.changeBodyType(specIndex, value));
        formGroup.get('fuelType').valueChanges.subscribe(value => this.changeFuelType(specIndex, value));
        formGroup.get('additionalInformation').valueChanges.subscribe(value => this.changeAdditionalInformation(specIndex, value));
        formGroup.get('additionalInformation').setValidators(Validators.maxLength(500));

        return formGroup;
    }

    addNewCarSpec(): void {
        const carSpecIndex = this.carSpecs.length;
        const formGroup = this.createNewCarSpec(carSpecIndex);
        this.carSpecs.push(formGroup);
        const leasingCarCriteria = new LeasingCarCriteria();
        leasingCarCriteria.searchResult = this.searchResult;
        this.leasingCriteria.carSpecs.push(leasingCarCriteria);

        //reset make for others cars than first one that has make preset
        if ((this.leasingCriteria.singleCarMake || this.presetCarWasRemoved) && carSpecIndex !== 0) {
            this.resetMake(carSpecIndex);
        }
        this.reloadCarOptions(carSpecIndex, null);

        const sectionId = 'carSpec_' + (this.carSpecs.length - 1);
        this.openSection(sectionId);

        // pospone scroll till the section is opened
        setTimeout(() => {
            let el = document.getElementById(sectionId);
            if (carSpecIndex === 0) {
                el = document.getElementById('carSpecsStart');
            }
            el.scrollIntoView({behavior: 'smooth'});
        }, 100);

    }

    removeCarSpec(index: number): void {
        this.carSpecs.removeAt(index);
        this.leasingCriteria.carSpecs.splice(index, 1);
        this.disableOwnCarIfNeeded();

        //if the pre-seated make and model is removed, reset single make and single model fields
        if (this.leasingCriteria.singleCarMake && this.leasingCriteria.singleCarModel && index === 0) {
            this.leasingCriteria.singleCarMake = null;
            this.leasingCriteria.singleCarModel = null;
            this.presetCarWasRemoved = true;
        }
        //reindex the carSpecs form
        this.reindexCarSpecForm(index);

    }

    reindexCarSpecForm(index: number): void {
        for (let i = index; i < this.leasingCriteria.carSpecs.length; i++) {
            const formGroup = this.carSpecs.at(i);
            formGroup.get('make').valueChanges.subscribe(value => this.changeMake(i, value));
            formGroup.get('model').valueChanges.subscribe(value => this.changeModel(i, value));
            formGroup.get('bodyType').valueChanges.subscribe(value => this.changeBodyType(i, value));
            formGroup.get('fuelType').valueChanges.subscribe(value => this.changeFuelType(i, value));
            formGroup.get('additionalInformation').valueChanges.subscribe(value => this.changeAdditionalInformation(i, value));
        }

    }

    carSpecAt(specIndex: number): AbstractControl {
        return this.carSpecs.at(specIndex);
    }

    carSpecServiceControls(specIndex: number): AbstractControl[] {
        return (this.carSpecs.at(specIndex).get('services') as FormArray).controls;
    }

    carSpecService(specIndex: number, serviceIndex: number): IIdNameItem {
        return this.leasingCriteria.carSpecs[specIndex].serviceOptions[serviceIndex];
    }

    createContactPersonGroup(): FormGroup {
        const formGroup: FormGroup = this.fb.group({
            hasOwnCar: [{value: true, disabled: false}],
            ownCar: this.fb.group({
                registrationNumber: [{value: '', disabled: false}],
                mileage: [{value: null, disabled: false}],
                additionalInformation: [{value: null, disabled: false}, [Validators.maxLength(2000)]]
            }),
            name: [{value: '', disabled: false}, Validators.required],
            emailAddress: [{value: '', disabled: false}, [Validators.required, Validators.email]],
            phoneNumber: [{
                value: '',
                disabled: false
            }, [Validators.required, Validators.pattern(/^[- +()]*[0-9][- +()0-9]*$/)]],
            contactMethod: this.fb.group({
                contactMethodPhone: [{value: true, disabled: false}],
                contactMethodEmail: [{value: true, disabled: false}],
                prefferedPhoneTime: [{value: '', disabled: false}],
            })
        });

        formGroup.get('hasOwnCar').valueChanges.subscribe(value => this.changeHasOwnCar(value));
        formGroup.get('ownCar.registrationNumber').valueChanges.subscribe(value => this.changeOwnCarRegistrationNumber(value));
        formGroup.get('ownCar.mileage').valueChanges.subscribe(value => this.changeOwnCarMileage(value));
        formGroup.get('ownCar.additionalInformation').valueChanges.subscribe(value => this.changeOwnCarAdditionalInformation(value));
        formGroup.get('name').valueChanges.subscribe(value => this.changeContactPersonName(value));
        formGroup.get('emailAddress').valueChanges.subscribe(value => this.changeContactPersonEmail(value));
        formGroup.get('phoneNumber').valueChanges.subscribe(value => this.changeContactPersonPhone(value));
        formGroup.get('contactMethod.contactMethodPhone').valueChanges.subscribe(value => this.changeContactMethodPhone(value));
        formGroup.get('contactMethod.contactMethodEmail').valueChanges.subscribe(value => this.changeContactMethodEmail(value));
        formGroup.get('contactMethod.prefferedPhoneTime').valueChanges.subscribe(value => this.changePrefferedPhoneTime(value));
        return formGroup;
    }

    changeLocation(locationId: number): void {
        if (this.leasingCriteria.location == null && locationId != null) {
            this.setSearchLocation(locationId);
            return;
        }

        if (this.leasingCriteria.location && this.leasingCriteria.location.id !== locationId) {
            const confirmationDialogue = this.dialog.open(ChangeLocationDialogComponent);

            confirmationDialogue.afterClosed().subscribe(confirmation => {
                if (confirmation) {
                    this.setSearchLocation(locationId);
                } else {
                    // resetting location to its previous selection
                    this.leasingForm.get('location').setValue(this.leasingCriteria.location.id);
                }
            });
        }
    }

    setSearchLocation(locationId: number) {
        this.leasingCriteria.location = this.formData.municipalities.find(location => location.id === locationId);

        // reset elements below it: controls are reset do their initial value
        const controlsToReset: AbstractControl[] = this.findNextSiblingControls(this.leasingForm, 'location');
        this.resetControls(controlsToReset);

        this.leasingService.searchLeasingCustomers(this.leasingCriteria).subscribe({
            next: searchResult => {
                this.searchResult = searchResult;
                this.monthlyFeeSliderConfig.value = searchResult.search.monthlyFeeRange.min;
                this.monthlyFeeSliderConfig.min = searchResult.search.monthlyFeeRange.min;
                this.monthlyFeeSliderConfig.max = searchResult.search.monthlyFeeRange.max;
                this.monthlyFeeSliderConfig.overMin = searchResult.search.monthlyFeeRange.overMin;
                this.monthlyFeeSliderConfig.overMax = searchResult.search.monthlyFeeRange.overMax;
            },
            error: error => this.showErrorFeedback(error),
        });

        this.closeSection('location');
        this.openSection('monthlyFee');
    }

    changeMonthlyFee(monthlyFee: number): void {
        if (this.leasingCriteria.monthlyFee == null && monthlyFee !== 0) {
            this.setSearchMonthlyFee(monthlyFee);

            //when the user selects the monthly budget for the first time (and only for the first time).
            //we should display a pop up with some information. If the budgetClick variable is on local storage,
            //we should not display the pop-up anymore.
            if (localStorage.getItem('budgetClick') === null) {
                this.dialog.open(FirstClickBudgetDialogComponent);
                localStorage.setItem('budgetClick', '');
            }

            return;
        }

        if (this.leasingCriteria.monthlyFee !== monthlyFee) {
            const confirmationDialogue = this.dialog.open(ChangeBudgetDialogComponent);

            confirmationDialogue.afterClosed().subscribe(confirmation => {
                if (confirmation) {
                    this.setSearchMonthlyFee(monthlyFee);
                }
            });
        }
    }

    setSearchMonthlyFee(monthlyFee: number) {
        this.leasingForm.get('monthlyFee').setValue(monthlyFee);
        this.leasingCriteria.monthlyFee = monthlyFee;

        // reset elements below it: controls are reset do their initial value
        const controlsToReset: AbstractControl[] = this.findNextSiblingControls(this.leasingForm, 'monthlyFee');
        this.resetControls(controlsToReset);

        this.leasingService.searchLeasingCustomers(this.leasingCriteria).subscribe({
            next: searchResult => {
                this.searchResult = searchResult;

                this.carSpecs.reset();
                this.addNewCarSpec();
                this.leasingForm.get('carSpecs').enable();

                this.closeSection('monthlyFee');
                this.openSection('carSpecs');
            },
            error: error => this.showErrorFeedback(error),
        });
    }

    reloadCarOptions(specIndex: number, triggerFieldName: string) {
        const carCriteria = this.leasingCriteria.carSpecs[specIndex];
        const searchResult = carCriteria.searchResult.search;

        if (triggerFieldName !== 'contractLength') {
            carCriteria.contractLengthSliderConfig.step = 12;
            carCriteria.contractLengthSliderConfig.unit = 'kk';
            carCriteria.contractLengthSliderConfig.min = searchResult.contractLengthRange.min;
            carCriteria.contractLengthSliderConfig.max = searchResult.contractLengthRange.max;
            carCriteria.contractLengthSliderConfig.overMin = searchResult.contractLengthRange.overMin;
            carCriteria.contractLengthSliderConfig.overMax = searchResult.contractLengthRange.overMax;
        }
        if (triggerFieldName !== 'contractMileage') {
            carCriteria.contractMileageSliderConfig.step = 5000;
            carCriteria.contractMileageSliderConfig.unit = 'km';
            carCriteria.contractMileageSliderConfig.min = searchResult.contractMileageRange.min;
            carCriteria.contractMileageSliderConfig.max = searchResult.contractMileageRange.max;
            carCriteria.contractMileageSliderConfig.overMin = searchResult.contractMileageRange.overMin;
            carCriteria.contractMileageSliderConfig.overMax = searchResult.contractMileageRange.overMax;
        }
        if (triggerFieldName !== 'model') {
            carCriteria.models = carCriteria.allMakeModels.filter(
                model => searchResult.models.includes(model.id));
        }
        if (triggerFieldName !== 'bodyType') {
            carCriteria.bodyTypes = this.formData.bodyTypes.filter(
                bodyType => searchResult.bodyTypes.includes(bodyType.id));
        }
        if (triggerFieldName !== 'fuelType') {
            carCriteria.fuelTypes = this.formData.fuelTypes.filter(
                fuelType => searchResult.fuelTypes.includes(fuelType.id));
        }
        if (triggerFieldName !== 'services') {
            carCriteria.serviceOptions = this.formData.services.filter(
                service => searchResult.services.includes(service.id));
            this.reloadServices(specIndex);
        }
        // makes must be changed every time
        carCriteria.makes = this.formData.makes.filter(
            make => searchResult.makes.includes(make.id));

        // autoselect make and model if single municipality-make-model mode for first car
        if (specIndex === 0) {
            this.autoselectCarOptions(specIndex);
        }

        // set default contract legth if slider is disabled and add/remove validators
        const contractLength = this.carSpecs.at(specIndex).get('contractLength') as FormControl;
        contractLength.clearValidators();
        if (carCriteria.contractLengthSliderConfig.min == carCriteria.contractLengthSliderConfig.max || carCriteria.contractLengthSliderConfig.value <= 0) {
			if (carCriteria.contractLengthSliderConfig.min > 0) {
	            carCriteria.contractLengthSliderConfig.value = carCriteria.contractLengthSliderConfig.min;
	            carCriteria.contractLength = carCriteria.contractLengthSliderConfig.min;
	            searchResult.contractLengthRange.value = carCriteria.contractLengthSliderConfig.min;
	        }
        }
        
        contractLength.updateValueAndValidity();

        // set default contract mileage if slider is disabled and add/remove validators
        const contractMileage = this.carSpecs.at(specIndex).get('contractMileage') as FormControl;
        contractMileage.clearValidators();
        if (carCriteria.contractMileageSliderConfig.min == carCriteria.contractMileageSliderConfig.max || carCriteria.contractMileageSliderConfig.value <= 0) {
			if (carCriteria.contractMileageSliderConfig.min > 0) {
	            carCriteria.contractMileageSliderConfig.value = carCriteria.contractMileageSliderConfig.min;
	            carCriteria.contractMileage = carCriteria.contractMileageSliderConfig.min;
	            searchResult.contractMileageRange.value = carCriteria.contractMileageSliderConfig.min;
	        }
        }
        contractLength.updateValueAndValidity();

        this.disableOwnCarIfNeeded();
    }

    resetCarSpecField(specIndex: number, fieldName: string) {
        this.carSpecAt(specIndex).get(fieldName).reset();
        this.leasingCriteria.carSpecs[specIndex][fieldName] = null;
        this.leasingService.searchLeasingCustomers(this.leasingCriteria, specIndex).subscribe({
            next: searchResult => {
                this.leasingCriteria.carSpecs[specIndex].searchResult = searchResult;
                // reload all options including current field
                this.reloadCarOptions(specIndex, null);
            },
            error: error => this.showErrorFeedback(error),
        });
    }

    autoselectMunicipality() {
        if (!this.leasingCriteria.singleMunicipality) {
            return;
        }

        // in single municipality-make-model mode auto-select the single municipality
        this.leasingForm.get('location').setValue(this.leasingCriteria.singleMunicipality);
        this.leasingForm.get('location').disable();
    }

    autoselectCarOptions(specIndex: number) {
        if (!this.leasingCriteria.singleCarMake || !this.leasingCriteria.singleCarModel) {
            return;
        }

        // in single municipality-make-model mode auto-select the single make and model
        if (this.leasingCriteria.carSpecs[specIndex]['make'] == null || this.leasingCriteria.singleCarMake != this.leasingCriteria.carSpecs[specIndex]['make'].id) {
            this.carSpecs.at(specIndex).get('make').setValue(this.leasingCriteria.singleCarMake);
        }
        if (this.leasingCriteria.carSpecs[specIndex]['model'] == null || this.leasingCriteria.singleCarModel != this.leasingCriteria.carSpecs[specIndex]['model'].id) {
            this.carSpecs.at(specIndex).get('model').setValue(this.leasingCriteria.singleCarModel);
        }

        //disable make and model if single make and model only once to avoid infinite loop due the fact that when
        // changing make or model triggers another reloadCarOptions
        if (this.leasingCriteria.carSpecs[specIndex]['make'] != null &&
            this.leasingCriteria.singleCarMake == this.leasingCriteria.carSpecs[specIndex]['make'].id &&
            this.leasingCriteria.carSpecs[specIndex]['model'] != null &&
            this.leasingCriteria.singleCarModel == this.leasingCriteria.carSpecs[specIndex]['model'].id &&
            !this.makeAndModelWasDisabled) {
            this.carSpecs.at(specIndex).get('make').disable();
            this.carSpecs.at(specIndex).get('model').disable();
            this.makeAndModelWasDisabled = true;
        }
    }

    changeContractLength(specIndex: number, contractLength: number): void {
        if (contractLength != null) {
            const carCriteria = this.leasingCriteria.carSpecs[specIndex];
            carCriteria.contractLength = contractLength;

            this.leasingService.searchLeasingCustomers(this.leasingCriteria, specIndex).subscribe({
                next: searchResult => {
                    carCriteria.searchResult = searchResult;
                    this.reloadCarOptions(specIndex, 'contractLength');
                },
                error: error => this.showErrorFeedback(error),
            });
        }
    }

    changeContractMileage(specIndex: number, contractMileage: number): void {
        if (contractMileage != null) {
            const carCriteria = this.leasingCriteria.carSpecs[specIndex];
            carCriteria.contractMileage = contractMileage;

            this.leasingService.searchLeasingCustomers(this.leasingCriteria, specIndex).subscribe({
                next: searchResult => {
                    carCriteria.searchResult = searchResult;
                    this.reloadCarOptions(specIndex, 'contractMileage');
                },
                error: error => this.showErrorFeedback(error),
            });
        }
    }

    resetMake(specIndex: number): void {
        this.leasingCriteria.carSpecs[specIndex].model = null;
        this.leasingCriteria.carSpecs[specIndex].models = [];
        this.carSpecAt(specIndex).get('model').reset();
        this.resetCarSpecField(specIndex, 'make');
    }

    changeMake(specIndex: number, makeId: number): void {
        if (makeId != null) {
            const carCriteria = this.leasingCriteria.carSpecs[specIndex];
            carCriteria.make = this.formData.makes.find(make => make.id === makeId);

            this.leasingService.searchLeasingCustomers(this.leasingCriteria, specIndex).subscribe({
                next: searchResult => {
                    carCriteria.searchResult = searchResult;
                    this.leasingService.getCarModels(makeId).subscribe({
                        next: makeModels => {
                            this.leasingCriteria.carSpecs[specIndex].allMakeModels = makeModels[0].models;
                            this.reloadCarOptions(specIndex, 'make');
                        },
                        error: error => this.showErrorFeedback(error),
                    });
                },
                error: error => this.showErrorFeedback(error),
            });
        }
    }

    changeModel(specIndex: number, carModelId: number): void {
        if (carModelId != null) {
            const carCriteria = this.leasingCriteria.carSpecs[specIndex];
            carCriteria.model = this.leasingCriteria.carSpecs[specIndex].allMakeModels.find(
                carModel => carModel.id === carModelId);

            this.leasingService.searchLeasingCustomers(this.leasingCriteria, specIndex).subscribe({
                next: searchResult => {
                    carCriteria.searchResult = searchResult;
                    this.reloadCarOptions(specIndex, 'model');
                },
                error: error => this.showErrorFeedback(error),
            });
        }
    }

    changeBodyType(specIndex: number, bodyTypeId: number): void {
        if (bodyTypeId != null) {
            const carCriteria = this.leasingCriteria.carSpecs[specIndex];
            carCriteria.bodyType = this.formData.bodyTypes.find(
                bodyType => bodyType.id === bodyTypeId);

            this.leasingService.searchLeasingCustomers(this.leasingCriteria, specIndex).subscribe({
                next: searchResult => {
                    carCriteria.searchResult = searchResult;
                    this.reloadCarOptions(specIndex, 'bodyType');
                },
                error: error => this.showErrorFeedback(error),
            });
        }
    }

    changeFuelType(specIndex: number, fuelTypeId: number): void {
        if (fuelTypeId != null) {
            const carCriteria = this.leasingCriteria.carSpecs[specIndex];
            carCriteria.fuelType = this.formData.fuelTypes.find(
                fuelType => fuelType.id === fuelTypeId);

            this.leasingService.searchLeasingCustomers(this.leasingCriteria, specIndex).subscribe({
                next: searchResult => {
                    carCriteria.searchResult = searchResult;
                    this.reloadCarOptions(specIndex, 'fuelType');
                },
                error: error => this.showErrorFeedback(error),
            });
        }
    }

    reloadServices(specIndex: number) {
        const servicesFormArray = (this.carSpecAt(specIndex).get('services') as FormArray);

        const oldSelectedServices = this.leasingCriteria.carSpecs[specIndex].services as IIdNameItem[];

        servicesFormArray.clear();
        if (this.leasingCriteria.carSpecs[specIndex]) {
            this.leasingCriteria.carSpecs[specIndex].services = [];
        }

        this.leasingCriteria.carSpecs[specIndex].serviceOptions.forEach((_service, index) => {
            const serviceCheckbox: FormControl = new FormControl();
            const selected = oldSelectedServices.find(service => service.id === _service.id);
            if (selected) {
                serviceCheckbox.setValue(true);
                this.leasingCriteria.carSpecs[specIndex].services.push(_service);
            }
            serviceCheckbox.valueChanges.subscribe(value => this.changeServices(
                specIndex, this.leasingCriteria.carSpecs[specIndex], index, value));
            servicesFormArray.push(serviceCheckbox);
        });
    }

    changeServices(specIndex: number, leasingCarCriteria: LeasingCarCriteria, checkboxIndex: number, value: any): void {
        if (leasingCarCriteria && value != null) {
            const service: IIdNameItem = leasingCarCriteria.serviceOptions[checkboxIndex];
            const carCriteria = this.leasingCriteria.carSpecs[specIndex];
            if (value) {
                leasingCarCriteria.services.push(service);
            } else {
                leasingCarCriteria.services.splice(leasingCarCriteria.services.indexOf(service), 1);
            }
            this.leasingService.searchLeasingCustomers(this.leasingCriteria, specIndex).subscribe({
                next: searchResult => {
                    carCriteria.searchResult = searchResult;
                    this.reloadCarOptions(specIndex, 'services');
                },
            });
        }
    }

    changeAdditionalInformation(specIndex: number, additionalInformation: string): void {
        if (this.leasingCriteria.carSpecs[specIndex]) {
            this.leasingCriteria.carSpecs[specIndex].additionalInformation = additionalInformation;
        }
    }

    addOwnCarValidators(hasOwnCar: boolean) {
        const registrationNumber = this.leasingForm.get('contactPerson.ownCar.registrationNumber') as FormControl;
        const mileage = this.leasingForm.get('contactPerson.ownCar.mileage') as FormControl;
        const additionalInfo = this.leasingForm.get('contactPerson.ownCar.additionalInformation') as FormControl;
        if (hasOwnCar) {
            registrationNumber.setValidators([Validators.required]);
            mileage.setValidators([Validators.required, Validators.pattern(/^-?(0|[1-9]\d*)?$/)]);
            additionalInfo.setValidators([Validators.maxLength(2000)]);
        } else {
            registrationNumber.clearValidators();
            mileage.clearValidators();
            additionalInfo.clearValidators();
        }
        registrationNumber.updateValueAndValidity();
        mileage.updateValueAndValidity();
        additionalInfo.updateValueAndValidity();
    }

    changeHasOwnCar(value: string): void {
        const hasOwnCar = (value === 'true');
        if (hasOwnCar && !this.allCustomersAcceptUsedCars) {
            this.dialog.open(UsedCarsNotAcceptedDialogComponent, {
                data: {customerNames: this.customerNamesNotAcceptingUsedCars},
            });
        }
        this.leasingCriteria.contactPerson.hasOwnCar = hasOwnCar;
        this.addOwnCarValidators(hasOwnCar);
    }

    changeOwnCarRegistrationNumber(registrationNumber: string): void {
        if (registrationNumber) {
            this.leasingCriteria.contactPerson.ownCarRegistrationNumber = registrationNumber;
        }
    }

    changeOwnCarMileage(mileage: number): void {
        if (mileage) {
            this.leasingCriteria.contactPerson.ownCarMileage = mileage;
        }
    }

    changeOwnCarAdditionalInformation(additionalInfo: string): void {
        if (additionalInfo) {
            this.leasingCriteria.contactPerson.ownCarAdditionalInformation = additionalInfo;
        }
    }

    disableOwnCarIfNeeded(): void {
        if (this.noCustomerAcceptsUsedCars) {
            this.changeHasOwnCar('false');
        }
    }

    changeContactPersonName(name: string): void {
        if (name) {
            this.leasingCriteria.contactPerson.name = name;
        }
    }

    changeContactPersonEmail(emailAddress: string): void {
        if (emailAddress) {
            this.leasingCriteria.contactPerson.emailAddress = emailAddress;
        }
    }

    changeContactPersonPhone(phoneNumber: string): void {
        if (phoneNumber) {
            this.leasingCriteria.contactPerson.phoneNumber = phoneNumber;
        }
    }

    changeContactMethodPhone(value: boolean): void {
        this.leasingCriteria.contactPerson.contactMethodPhone = value;
    }

    changeContactMethodEmail(value: boolean): void {
        this.leasingCriteria.contactPerson.contactMethodEmail = value;
    }

    changePrefferedPhoneTime(value: string): void {
        this.leasingCriteria.contactPerson.prefferedPhoneTime = value;
    }

    searchLeasingCustomers(specIndex: number = null) {
        this.leasingService.searchLeasingCustomers(this.leasingCriteria, specIndex).subscribe({
            next: searchResult => this.searchResult = searchResult,
            error: error => this.showErrorFeedback(error),
        });
    }

    isContactDetailsEnabled() {
        if (this.carSpecs.length === 0) {
            return false;
        }
        let oneCarSpecValid = false;
        this.carSpecs.controls.forEach(carSpec => {
            if (carSpec.valid) {
                oneCarSpecValid = true;
                return;
            }
        });
        return oneCarSpecValid;
    }

    controlError(messagePath: string, control: AbstractControl): string {
        const errorMessages: string[] = [];
        if (control.touched && control.invalid) {
            Object.keys(control.errors).forEach(keyError => {
                const pathSegments = messagePath.split('.');
                pathSegments.push(keyError);
                errorMessages.push(this.getMessage(pathSegments));
            });
        }
        if (errorMessages.length > 0) {
            return errorMessages[0];
        }
        return null;
    }

    getMessage(pathSegments: string[]): string {
        let obj: object = Messages.LEASING_COMPONENT;
        pathSegments.forEach(pathSegment => {
            obj = obj[pathSegment];
        });
        return obj.toString();
    }

    /**
     * Finds the form controls defined below the control with controlName element.
     * The search is done within a formGroup provided as the first argument.
     * Only direct controls are returned, the children controls are not searched.
     *
     * @param formGroup the form group where to search
     * @param controlName the name of the control
     */
    findNextSiblingControls(formGroup: FormGroup, controlName: string): AbstractControl[] {
        const controls: AbstractControl[] = [];
        let addControl = false;
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.get(key);
            if (addControl && !this.isIgnoredOnReset(key)) {
                controls.push(control);
            }
            if (controlName === key) {
                addControl = true;
            }
        });
        return controls;
    }

    /**
     * Checks if a control name should be ignored when finding below controls.
     * TODO: it will be better if the elements to be ignored are marked in the template.
     *
     * @param controlName the name of the form control to be checked.
     */
    isIgnoredOnReset(controlName: string): boolean {
        return this.ignoredControlNamesOnReset.includes(controlName);
    }

    /**
     * Resets controls to their initial value. If there are nested groups, their children are also reset.
     * Form arrays are cleared and its first group is reset.
     *
     * @param controls the controls to be reset
     */
    resetControls(controls: AbstractControl[]) {
        controls.forEach(control => {
            if (control instanceof FormGroup) {
                const controlsToReset = Object.keys(control.controls).map(key => control.controls[key]);
                this.resetControls(controlsToReset);
                return;
            }

            if (control instanceof FormArray) {
                const controlsToReset = Object.keys(control.controls).map(key => control.controls[key]);
                this.resetControls(controlsToReset);
                control.clear();
                return;
            }

            control.reset();
        });
    }

    viewSummary() {
        const summaryDialog = this.dialog.open(SummaryDialogComponent, {
            data: {leasingComponent: this},
        });
    }

    submit() {
        if (this.leasingForm.invalid) {
            this.markFormGroupTouched(this.leasingForm);
            this.openInvalidSections();
            this.showFeedback('error', Messages.LEASING_COMPONENT.general.error.title, Messages.LEASING_COMPONENT.general.invalidForm);
            return;
        }
        this.leasingService.submitLeasingCustomers(this.leasingCriteria).subscribe({
            next: success => {
                if (success) {
                    onSubmitDone();
                    let message = Messages.LEASING_COMPONENT.general.success.message + this.allCustomerNames;
                    if (this.leasingCriteria.contactPerson.hasOwnCar) {
                        message = Messages.LEASING_COMPONENT.general.success.message + this.customerNamesAcceptingUsedCars;
                    }
                    this.ngOnInit();
                    this.showFeedback('success', Messages.LEASING_COMPONENT.general.success.title, message);
                } else {
                    this.showFeedback('error', Messages.LEASING_COMPONENT.general.error.title, Messages.LEASING_COMPONENT.general.error.message);
                }
            },
            error: error => {
                this.showErrorFeedback(error);
            }
        });
    }

    openInvalidSections() {
        if (this.leasingForm.get('location').invalid) {
            this.openSection('location');
        }
        if (this.leasingCriteria.location && this.leasingForm.get('monthlyFee').invalid) {
            this.openSection('monthlyFee');
        }
        if (this.leasingCriteria.monthlyFee && this.carSpecs.invalid) {
            this.openSection('carSpecs');
            this.carSpecs.controls.forEach((carSpec, index) => {
                if (carSpec.invalid) {
                    this.openSection('carSpec_' + index);
                }
            });
        }
        if (this.isContactDetailsEnabled() && this.leasingForm.get('contactPerson').invalid) {
            this.openSection('contactPerson');
        }
    }

    showFeedback(_feedbackLevel: string, _title: string, _message: string) {
        this.dialog.open(FeedbackDialogComponent, {
            data: {level: _feedbackLevel, title: _title, message: _message},
        });
    }

    showErrorFeedback(error: Error) {
        this.showFeedback('error', Messages.LEASING_COMPONENT.general.error.title, Messages.LEASING_COMPONENT.general.error.message);
    }

    /**
     * Marks all controls in a form group as touched
     * @param formGroup - The form group to touch
     */
    private markFormGroupTouched(formGroup: FormGroup) {
        const _this = this;
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.controls[key] as any;
            control.markAsTouched();
            if (control.controls) {
                _this.markFormGroupTouched(control);
            }
        });

        this.leasingCriteria.carSpecs.forEach(carSpec => {
            carSpec.contractLengthSliderConfig.dirty = true;
            carSpec.contractMileageSliderConfig.dirty = true;
        });

    }

    hideUsedCarTooltip(e) {
        this.usedCarTooltipVisible = false;
        e.preventDefault();
        return false;
    }

    isLocationTooltipVisible(): boolean {
        if (this.leasingCriteria.location && this.locationTooltipVisible) {
            return true;
        }
        return false;
    }

    hideLocationTooltip(e) {
        this.locationTooltipVisible = false;
        e.preventDefault();
        return false;
    }

    isServiceTooltipVisible(): boolean {
        if (this.serviceTooltipVisible) {
            localStorage.setItem('serviceClick', '');
            return true;
        }
        return false;
    }

    hideServiceTooltip(e) {
        this.serviceTooltipVisible = false;
        e.preventDefault();
        return false;
    }

    onServiceChange(e) {
        if (screen.width < 600) {
            // on mobiles the tooltip is not needed
            this.serviceTooltipVisible = false;
            return;
        }
        const checkbox = document.getElementById(e.source.id);
        const left = checkbox.offsetLeft;
        const width = checkbox.offsetWidth;
        const top = checkbox.offsetTop;
        const tooltip = document.getElementById('serviceTooltipContainer');

        //25 is the position of the arrow
        tooltip.style.top = (top - 25) + 'px';
        // +20px because 10 is the width of the arrow, and 10 a margin
        tooltip.style.left = (left + width + 20) + 'px';

        if (localStorage.getItem('serviceClick') === null) {
            this.serviceTooltipVisible = true;
            e.preventDefault();
        }
    }

    isSectionOpened(sectionName: string) {
        return this.openedSections.indexOf(sectionName) >= 0;
    }

    openSection(sectionName: string) {
        if (!this.isSectionOpened(sectionName)) {
            this.openedSections.push(sectionName);
        }
    }

    closeSection(sectionName: string) {
        if (this.isSectionOpened(sectionName)) {
            this.openedSections.splice(this.openedSections.indexOf(sectionName), 1);
        }
    }

    formatSliderMonthlyFeeLabel(value: number) {
        return value + ' €';
    }

    formatSliderContractLengthLabel(value: number) {
        return value + ' kk';
    }

    formatSliderContractMileageLabel(value: number) {
        return (value / 1000) + ' tkm';
    }

    getSliderSteps(slider: ISliderConfig) {
        const steps: string[] = [];

        let min = slider.getRoundedMin();
        let max = slider.getRoundedMax();

        steps.push(min + '' + slider.unit);
        steps.push(max + '' + slider.unit);

        return steps;
    }

    updateSliderValue(event: Event) {
        return +(event.target as HTMLInputElement).value;
    }

    goBack() {
        window.history.back()
    }
}
